import { Injectable } from '@angular/core';
import { FeatureFlagControllerService } from '@app/generated/service';
import { UserFeatureDto } from '@app/generated/model';
import { FeatureFlagKey } from '@dwo/enums';
import { firstValueFrom } from 'rxjs';

// Loaded once per session at startup; last result is cached so flags stay available offline.
const CACHE_KEY = 'dwo.featureFlags';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private flags = new Map<string, UserFeatureDto>();

  constructor(private featureFlagController: FeatureFlagControllerService) {
    this.hydrateFromCache();
  }

  async load(username: string): Promise<void> {
    if (!username) {
      return;
    }
    try {
      const features = await firstValueFrom(this.featureFlagController.getUserFeatures(username));
      // The global HTTP error interceptor can emit error responses as values.
      if (!Array.isArray(features)) {
        return;
      }
      this.setFlags(features);
      localStorage.setItem(CACHE_KEY, JSON.stringify(features));
    } catch {
      // Startup should keep working with cached or default-disabled flags.
    }
  }

  isEnabled(key: FeatureFlagKey, projectId?: string): boolean {
    const feature = this.flags.get(key);
    if (!feature?.enabled) {
      return false;
    }

    const selectedProjects = feature.onlySelectedProjects ?? [];
    if (selectedProjects.length === 0) {
      return true;
    }

    if (!projectId) {
      return false;
    }

    return selectedProjects.includes(projectId);
  }

  private setFlags(features: UserFeatureDto[]): void {
    this.flags = new Map((features ?? []).filter((feature) => !!feature.key).map((feature) => [feature.key, feature]));
  }

  private hydrateFromCache(): void {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return;
    }
    try {
      this.setFlags(JSON.parse(cached) as UserFeatureDto[]);
    } catch {
      localStorage.removeItem(CACHE_KEY);
    }
  }
}
